import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as any);

    try {
      await fetch('https://readdy.ai/api/form/d7fikf6ivmjfhtdrg0c0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Info */}
          <div>
            <span className="inline-block text-xs tracking-widest uppercase text-sky-600 font-semibold mb-3">
              Contact Us
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5 leading-tight">
              We'd Love to<br />Hear From You
            </h2>
            <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-10">
              Whether you have questions about our services, want to schedule a tour, or are ready to begin the care journey — our team is here to help every step of the way.
            </p>

            {/* Contact Details */}
            <div className="space-y-5">
              {[
                { icon: 'ri-map-pin-line', label: 'Address', value: '7340 Scot Terrace, Eden Prairie, Minnesota' },
                { icon: 'ri-phone-line', label: 'Phone', value: '(612) 735-1448' },
                { icon: 'ri-mail-line', label: 'Email', value: 'PureCareassisted@gmail.com' },
                { icon: 'ri-time-line', label: 'Hours', value: 'Open 24 Hours, 7 Days a Week' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-sky-50 shrink-0 mt-0.5">
                    <i className={`${item.icon} text-sky-600 text-sm`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 mb-0.5">{item.label}</div>
                    <div className="text-sm text-gray-800">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Google Maps */}
            <div className="mt-10 rounded-2xl overflow-hidden border border-sky-100 h-52 w-full">
              <iframe
                title="Pure Care Assisted Living Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2826.9!2d-93.4567!3d44.8547!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87f618d1234abcd%3A0x1234567890abcdef!2s7340+Scot+Terrace%2C+Eden+Prairie%2C+MN+55346!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-sky-50 rounded-2xl p-8 border border-sky-100">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full min-h-64 text-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-sky-100">
                  <i className="ri-check-line text-sky-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Message Sent!</h3>
                <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
                  Thank you for reaching out. A member of our team will get back to you within one business day.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-xs text-sky-600 underline cursor-pointer mt-2 whitespace-nowrap"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Send Us a Message</h3>
                <p className="text-sm text-gray-400 mb-6">We typically respond within 24 hours.</p>

                <form
                  id="pure-care-contact"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        required
                        placeholder="Jane"
                        className="w-full text-sm px-4 py-2.5 rounded-lg border border-sky-100 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 placeholder:text-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        required
                        placeholder="Smith"
                        className="w-full text-sm px-4 py-2.5 rounded-lg border border-sky-100 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 placeholder:text-gray-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="jane@example.com"
                      className="w-full text-sm px-4 py-2.5 rounded-lg border border-sky-100 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 placeholder:text-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(612) 555-0100"
                      className="w-full text-sm px-4 py-2.5 rounded-lg border border-sky-100 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 placeholder:text-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">I'm interested in...</label>
                    <select
                      name="interest"
                      className="w-full text-sm px-4 py-2.5 rounded-lg border border-sky-100 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 text-gray-700 cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="tour">Scheduling a Tour</option>
                      <option value="services">Learning About Services</option>
                      <option value="pricing">Pricing &amp; Availability</option>
                      <option value="other">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <label className="block text-xs text-gray-500">Message</label>
                      <span className={`text-xs ${charCount > 450 ? 'text-red-400' : 'text-gray-400'}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="Tell us about your loved one's needs or ask any questions you may have..."
                      className="w-full text-sm px-4 py-2.5 rounded-lg border border-sky-100 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 resize-none placeholder:text-gray-300"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-full bg-sky-500 text-white text-sm font-medium hover:bg-sky-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
